/** React Query hooks for the Phase 5 landed-cost estimator. */
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDelete } from '../../../shared/api/axiosInstance';
import type { ShipmentEstimateList } from '../../../generated/api/models/shipmentEstimateList';
import type {
  BillEstimateVariance,
  EstimateCostSuggestionRequest,
  EstimateCostSuggestions,
  EstimateReconciliation,
  ShipmentEstimate,
  ShipmentEstimateCreate,
  ShipmentEstimateDraft,
  TariffLookupResponse,
} from '../types';

const ESTIMATES_API = '/api/v1/shipment-estimates';
const TARIFF_API = '/api/v1/shipments/reference-data/tariff-rates';

export const estimatesKeys = {
  all: ['shipment-estimates'] as const,
  list: (params?: EstimatesListParams) => ['shipment-estimates', 'list', params ?? null] as const,
  detail: (id: string) => ['shipment-estimates', 'detail', id] as const,
  reconciliation: (id: string) => ['shipment-estimates', 'reconciliation', id] as const,
  billVariance: (billId: string) => ['shipment-estimates', 'bill-variance', billId] as const,
  costSuggestions: (request: EstimateCostSuggestionRequest | null) =>
    ['shipment-estimates', 'cost-suggestions', request] as const,
};

/** Request shape of the server-paged estimates list (Shape A — ui-list-pages.md § 11). */
export interface EstimatesListParams {
  shipment_id?: string;
  purchase_order_id?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

/**
 * Estimates accumulate (one row per scenario, never pruned), so the list page
 * asks for ONE page and drives its pager from the response `total`. Returns the
 * whole response — callers that only want the rows read `data?.items`.
 */
export function useEstimates(params?: EstimatesListParams) {
  return useQuery({
    queryKey: estimatesKeys.list(params),
    // Search/paging state is in the query key: hold rows while the next page loads.
    placeholderData: keepPreviousData,
    queryFn: () => {
      const sp = new URLSearchParams();
      for (const [key, value] of Object.entries(params ?? {})) {
        if (value != null && value !== '') sp.set(key, String(value));
      }
      const qs = sp.toString();
      return apiGet<ShipmentEstimateList>(`${ESTIMATES_API}${qs ? `?${qs}` : ''}`);
    },
  });
}

export function useEstimate(id: string | undefined) {
  return useQuery({
    queryKey: estimatesKeys.detail(id ?? ''),
    queryFn: () => apiGet<ShipmentEstimate>(`${ESTIMATES_API}/${id}`),
    enabled: !!id,
  });
}

export function useEstimateReconciliation(id: string | undefined) {
  return useQuery({
    queryKey: estimatesKeys.reconciliation(id ?? ''),
    queryFn: () => apiGet<EstimateReconciliation>(`${ESTIMATES_API}/${id}/reconciliation`),
    enabled: !!id,
  });
}

/**
 * "How does this bill compare with what was estimated?" for the bill detail page
 * (landed-cost estimates E3).
 *
 * Answers for a bill on no shipment, or one whose shipment has no primary
 * estimate, with a null `estimate_id` rather than an error — the strip renders
 * nothing in those cases, which are the ordinary ones.
 */
export function useBillEstimateVariance(billId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: estimatesKeys.billVariance(billId ?? ''),
    queryFn: () => apiGet<BillEstimateVariance>(`${ESTIMATES_API}/bill-variance/${billId}`),
    enabled: !!billId && enabled,
  });
}

/**
 * The SHIPMENT-side reads that are derived from a shipment's estimates, rather
 * than from any one estimate: the Costs tab's blended per-category summary (only
 * the PRIMARY estimate feeds it) and the "no cost estimates yet" banner (its whole
 * condition is "there is no primary estimate"). Every estimate mutation moves at
 * least one of them, and none of the estimate query keys covers either — so
 * without this a create leaves the banner up over the estimate that just answered
 * it, and a promote moves the Primary badge while the summary above keeps quoting
 * the demoted worksheet. Two contradictory statements of one number on one screen
 * is the defect the Costs tab exists to end.
 *
 * Prefix-invalidated: these mutations know an estimate id, not a shipment id, and
 * an estimate can be re-pointed at a different shipment by the same PUT.
 */
function invalidateShipmentCostReads(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: ['shipments', 'cost-summary'] });
  qc.invalidateQueries({ queryKey: ['shipments', 'estimate-reminder'] });
}

export function useCreateEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ShipmentEstimateCreate) => apiPost<ShipmentEstimate>(ESTIMATES_API, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: estimatesKeys.all });
      invalidateShipmentCostReads(qc);
    },
  });
}

export function useUpdateEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ShipmentEstimateCreate }) =>
      apiPut<ShipmentEstimate>(`${ESTIMATES_API}/${id}`, data),
    onSuccess: (_r, { id }) => {
      qc.invalidateQueries({ queryKey: estimatesKeys.all });
      qc.invalidateQueries({ queryKey: estimatesKeys.detail(id) });
      qc.invalidateQueries({ queryKey: estimatesKeys.reconciliation(id) });
      invalidateShipmentCostReads(qc);
    },
  });
}

export function useDeleteEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`${ESTIMATES_API}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: estimatesKeys.all });
      invalidateShipmentCostReads(qc);
    },
  });
}

/**
 * Make this estimate its shipment's chosen baseline.
 *
 * Its own endpoint rather than a field on the update payload: promoting one
 * estimate has to demote the incumbent in the same transaction, which a
 * full-replace PUT cannot express.
 */
export function useSetPrimaryEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<ShipmentEstimate>(`${ESTIMATES_API}/${id}/primary`, {}),
    onSuccess: (_r, id) => {
      // The whole list: promoting this one demoted a sibling, so a cached row
      // elsewhere would still be wearing the Primary badge.
      qc.invalidateQueries({ queryKey: estimatesKeys.all });
      qc.invalidateQueries({ queryKey: estimatesKeys.detail(id) });
      invalidateShipmentCostReads(qc);
    },
  });
}

/**
 * One-shot auto-draft (not cached as a query — called on demand when the
 * estimator opens from a PO or a shipment).
 *
 * Persists nothing: the response is the body the create endpoint accepts, so the
 * form is pre-filled and the user decides whether an estimate exists at all.
 */
export function fetchEstimateDraft(params: {
  purchase_order_id?: string;
  shipment_id?: string;
}): Promise<ShipmentEstimateDraft> {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) sp.set(key, value);
  }
  return apiGet<ShipmentEstimateDraft>(`${ESTIMATES_API}/draft?${sp.toString()}`);
}

/** One-shot tariff lookup (not cached as a query — called on demand from the form). */
export function lookupTariff(
  hsCode: string,
  originCountry: string,
  destinationCountry: string,
): Promise<TariffLookupResponse> {
  const qs = new URLSearchParams({
    hs_code: hsCode,
    origin_country: originCountry,
    destination_country: destinationCountry,
  });
  return apiGet<TariffLookupResponse>(`${TARIFF_API}?${qs.toString()}`);
}

/**
 * What this tenant's OWN posted actuals say the lines being edited should cost.
 *
 * A POST that reads: the question is per-line and a worksheet asks about all of its
 * lines at once, which is the whole point — a per-line GET would be the N+1 the
 * feature's acceptance criteria forbid, and a list of line objects does not fit a
 * query string. It writes nothing, so it is a `useQuery` and the whole request is
 * the cache key.
 *
 * It is DISABLED until there is something to ask about, and it fails SILENTLY:
 * a suggestion is an offer, so a coordinator whose history is unreachable should see
 * the estimator they already had, not an error toast about a number they never asked
 * for. `retry: false` keeps a failing lookup from re-firing on every keystroke.
 */
export function useCostSuggestions(request: EstimateCostSuggestionRequest | null) {
  return useQuery({
    queryKey: estimatesKeys.costSuggestions(request),
    queryFn: () =>
      apiPost<EstimateCostSuggestions>(`${ESTIMATES_API}/cost-suggestions`, request),
    enabled: !!request && request.lines.length > 0,
    retry: false,
    // The worksheet's line set changes on every basis or leg edit, so hold the last
    // answer while the next one loads: an affordance that blinks out and back on each
    // keystroke reads as instability rather than as freshness.
    placeholderData: keepPreviousData,
    // History moves when a bill posts, not while somebody edits a worksheet.
    staleTime: 5 * 60 * 1000,
  });
}
